import AdminNav from '@/components/admin-nav';
import { admin_users_path } from '@/rails/routes';
import { formatDuration, formatShortDate } from '@/pages/reports/lib/format';
import { roleLabel, roleVariant } from '@/pages/admin/users/lib/roles';
import type { AdminUsersShow, WorkspacePageProps } from '@/types';
import { Badge } from '@astryxdesign/core/Badge';
import { Card } from '@astryxdesign/core/Card';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { Icon } from '@astryxdesign/core/Icon';
import { IconButton } from '@astryxdesign/core/IconButton';
import { Link } from '@astryxdesign/core/Link';
import { Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/VStack';
import { usePage } from '@inertiajs/react';
import { ArrowLeft, CalendarDays, ClipboardList, Users } from 'lucide-react';

type Props = WorkspacePageProps & AdminUsersShow;

function displayDate(date: string | null | undefined): string {
  return date ? formatShortDate(date.slice(0, 10)) : 'Sin partes';
}

const Show = () => {
  const { memberships, recentReports, user } = usePage<Props>().props;

  return (
    <VStack gap={6} width="100%" className="iorio-user-show-page">
      <section className="iorio-users-header" aria-labelledby="user-title">
        <VStack gap={3}>
          <Link href={admin_users_path()} aria-label="Volver a usuarios">
            <IconButton
              icon={<Icon icon={ArrowLeft} size="sm" />}
              variant="ghost"
              size="sm"
              label="Volver a usuarios"
              tooltip="Volver a usuarios"
            />
          </Link>

          <VStack gap={1}>
            <Heading level={1} id="user-title">
              {user.displayName}
            </Heading>
            <Text type="body" color="secondary">
              {user.email}
            </Text>
          </VStack>
        </VStack>
      </section>

      <AdminNav />

      <section className="iorio-user-show-grid" aria-label="Detalle de usuario">
        <Card padding={6} className="iorio-users-card iorio-user-profile-card">
          <VStack gap={5}>
            <HStack gap={2} vAlign="center">
              <span className="iorio-users-icon" aria-hidden="true">
                <Icon icon={Users} size="sm" />
              </span>
              <Heading level={2}>Perfil</Heading>
            </HStack>

            <dl className="iorio-user-detail-list">
              <div>
                <dt>Rol en este equipo</dt>
                <dd>
                  <Badge label={roleLabel(user.role)} variant={roleVariant(user.role)} />
                </dd>
              </div>
              <div>
                <dt>Partes cargados</dt>
                <dd>{user.reportsCount}</dd>
              </div>
              <div>
                <dt>Último parte</dt>
                <dd>{displayDate(user.lastReportDate)}</dd>
              </div>
              <div>
                <dt>Alta</dt>
                <dd>{displayDate(user.createdAt)}</dd>
              </div>
            </dl>
          </VStack>
        </Card>

        <Card padding={6} className="iorio-users-card">
          <VStack gap={5}>
            <HStack gap={2} vAlign="center">
              <span className="iorio-users-icon" aria-hidden="true">
                <Icon icon={CalendarDays} size="sm" />
              </span>
              <Heading level={2}>Equipos</Heading>
            </HStack>

            <ul className="iorio-user-memberships" aria-label="Equipos del usuario">
              {memberships.map((membership) => (
                <li key={membership.id} className="iorio-user-membership-row">
                  <HStack hAlign="between" vAlign="center" gap={3} wrap="wrap">
                    <VStack gap={0.5}>
                      <Text type="body" weight="bold">
                        {membership.teamName}
                      </Text>
                      <Text type="supporting" color="secondary">
                        Desde {displayDate(membership.createdAt)}
                      </Text>
                    </VStack>
                    <HStack gap={2} vAlign="center">
                      {membership.current && <Badge label="Actual" variant="teal" />}
                      <Badge
                        label={roleLabel(membership.role)}
                        variant={roleVariant(membership.role)}
                      />
                    </HStack>
                  </HStack>
                </li>
              ))}
            </ul>
          </VStack>
        </Card>
      </section>

      <Card padding={6} className="iorio-users-card">
        <VStack gap={5}>
          <HStack hAlign="between" vAlign="start" gap={3} wrap="wrap">
            <VStack gap={1}>
              <HStack gap={2} vAlign="center">
                <span className="iorio-users-icon" aria-hidden="true">
                  <Icon icon={ClipboardList} size="sm" />
                </span>
                <Heading level={2}>Partes recientes</Heading>
              </HStack>
              <Text type="supporting" color="secondary">
                Últimos diez días con versiones guardadas.
              </Text>
            </VStack>
            <Text type="supporting" color="secondary" hasTabularNumbers>
              {recentReports.length} visibles
            </Text>
          </HStack>

          {recentReports.length === 0 ? (
            <section className="iorio-users-empty" aria-label="Estado vacío de partes">
              <EmptyState
                title="Todavía no hay partes"
                description="Cuando esta persona guarde un parte diario, va a aparecer acá."
                icon={
                  <span className="iorio-users-icon" aria-hidden="true">
                    <Icon icon={ClipboardList} size="sm" />
                  </span>
                }
                isCompact
              />
            </section>
          ) : (
            <ul className="iorio-users-list" aria-label="Partes recientes del usuario">
              {recentReports.map((report) => (
                <li key={report.id} className="iorio-users-row">
                  <HStack hAlign="between" vAlign="center" gap={3} wrap="wrap">
                    <VStack gap={0.5} className="iorio-users-report-summary">
                      <Text type="body" weight="bold">
                        {formatShortDate(report.date)}
                      </Text>
                      <Text type="supporting" color="secondary" maxLines={2}>
                        {report.summary}
                      </Text>
                    </VStack>
                    <HStack gap={3} vAlign="center" wrap="wrap">
                      {report.planned && <Badge label="Planificado" variant="teal" />}
                      <Text type="supporting" color="secondary" hasTabularNumbers>
                        v{report.version ?? '-'}
                      </Text>
                      <Text type="supporting" color="secondary" hasTabularNumbers>
                        {formatDuration(report.minutes)}
                      </Text>
                    </HStack>
                  </HStack>
                </li>
              ))}
            </ul>
          )}
        </VStack>
      </Card>
    </VStack>
  );
};

export default Show;
